import { useAppSelector } from "integrations/redux/hooks"
import { BORDER_THICKNESS as EXPECTED_BORDER_THICKNESS } from "../PDFSnapshotContainer"
import {
    selectEnabled,
    selectBorderData,
    selectNotation,
    selectCoordinateSystem,
    selectColorTheme,
    selectPDFLegendScale,
} from "../PDFSlice"
import { WebMercatorViewport } from "@deck.gl/core"
import {
    coordinateSystems,
    getLinePoints,
    GridLine,
    GridVertex,
    projectCRS,
} from "./PDFCoordinateNotations"
import { Fragment, type JSX } from "react"

const getEdgeProps = (BORDER_THICKNESS: number) =>
    ({
        0: (x) => ({
            textAnchor: "middle",
            dominantBaseline: "central",
            transform: `rotate(0, ${x + BORDER_THICKNESS}, ${BORDER_THICKNESS / 2})`,
            x: x + BORDER_THICKNESS,
            y: BORDER_THICKNESS / 2,
        }),

        1: (y, width) => ({
            textAnchor: "middle",
            dominantBaseline: "central",
            transform: `rotate(90, ${width - BORDER_THICKNESS * 0.5}, ${y + BORDER_THICKNESS})`,
            x: width - BORDER_THICKNESS * 0.5,
            y: y + BORDER_THICKNESS,
        }),

        2: (x, height) => ({
            textAnchor: "middle",
            dominantBaseline: "central",
            transform: `rotate(0, ${x + BORDER_THICKNESS}, ${height - BORDER_THICKNESS * 0.5})`,
            x: x + BORDER_THICKNESS,
            y: height - BORDER_THICKNESS * 0.5,
        }),

        3: (y) => ({
            textAnchor: "middle",
            dominantBaseline: "central",
            transform: `rotate(270, ${BORDER_THICKNESS / 2}, ${y + BORDER_THICKNESS})`,
            x: BORDER_THICKNESS / 2,
            y: y + BORDER_THICKNESS,
        }),
    }) as Record<string, (num: number, size: number) => Record<string, string | number>>
type GridPoint = { vertex: GridVertex; tickType: 0 | 1; tick: string; coord: number[] }

function measureText(label: string, fontSize: number, factor = 0.55): number {
    return label.length * fontSize * factor
}

const filterLines = (
    lines: GridLine[],
    view: WebMercatorViewport,
    minX: number,
    minY: number,
    getTick: (coord: number[]) => string[],
    fontSize: number
) => {
    const getPointsOnEdge = (edge: number) => {
        return lines.reduce((acc, [a, b]) => {
            const coord1 = projectCRS(view.unproject([a.point[0] + minX, a.point[1] + minY]))
            const coord2 = projectCRS(view.unproject([b.point[0] + minX, b.point[1] + minY]))
            const tickType =
                Math.abs(coord1[0] - coord2[0]) < Math.abs(coord1[1] - coord2[1]) ? 0 : 1
            const tick = getTick(coord1)[tickType]
            if (a.edge === edge) acc.push({ vertex: a, tick, tickType, coord: coord1 })
            if (b.edge === edge) acc.push({ vertex: b, tick, tickType, coord: coord2 })
            return acc
        }, [] as GridPoint[])
    }

    const filterNonDominantTickType = (points: GridPoint[]) => {
        const lngs = points.filter((e) => !e.tickType)
        const lats = points.filter((e) => e.tickType)
        const minLng = Math.min(...points.map((e) => e.coord[0]))
        const maxLng = Math.max(...points.map((e) => e.coord[0]))
        const minLat = Math.min(...points.map((e) => e.coord[1]))
        const maxLat = Math.max(...points.map((e) => e.coord[1]))
        const dominant = maxLng - minLng > maxLat - minLat ? lngs : lats
        const nonDominant = dominant === lngs ? lats : lngs
        const filteredNonDominant = nonDominant.filter((a) => {
            return !dominant.some((b) => {
                const distance = Math.max(
                    Math.abs(a.vertex.point[0] - b.vertex.point[0]),
                    Math.abs(a.vertex.point[1] - b.vertex.point[1])
                )
                return (
                    distance < measureText(a.tick, fontSize) / 2 + measureText(b.tick, fontSize) / 2
                )
            })
        })
        return [...dominant, ...filteredNonDominant]
    }

    const tops = getPointsOnEdge(0)
    const rights = getPointsOnEdge(1)
    const bottoms = getPointsOnEdge(2)
    const lefts = getPointsOnEdge(3)

    return [
        ...filterNonDominantTickType(tops),
        ...filterNonDominantTickType(rights),
        ...filterNonDominantTickType(bottoms),
        ...filterNonDominantTickType(lefts),
    ]
}

export const BorderData = ({
    width,
    height,
    view,
    offsetX,
    offsetY,
}: {
    width: number
    height: number
    view: WebMercatorViewport
    offsetX: number
    offsetY: number
}) => {
    const enabled = useAppSelector(selectEnabled())
    const borderData = useAppSelector(selectBorderData())
    const notation = useAppSelector(selectNotation())
    const coordinateSystem = useAppSelector(selectCoordinateSystem())
    const legendScale = useAppSelector(selectPDFLegendScale())
    const isDark = useAppSelector(selectColorTheme()) === "dark"
    const getTick = coordinateSystems[coordinateSystem].notations[notation].getTick

    if (!enabled.borderticks) return null
    const BORDER_THICKNESS = EXPECTED_BORDER_THICKNESS * legendScale
    const fontSize = legendScale * 7.63
    const edgeProps = getEdgeProps(BORDER_THICKNESS)
    const h = height - BORDER_THICKNESS * 2
    const w = width - BORDER_THICKNESS * 2
    const minX = offsetX + BORDER_THICKNESS
    const minY = offsetY + BORDER_THICKNESS

    const lines = getLinePoints(
        [
            [minX, minY],
            [minX + w, minY],
            [minX + w, minY + h],
            [minX, minY + h],
        ],
        view,
        borderData
    )
    const borderTicks: JSX.Element[] = []
    if (lines.length <= 100) {
        const points = filterLines(lines, view, minX, minY, getTick, fontSize)
        points.forEach(({ vertex, tick }, idx) => {
            borderTicks.push(
                <Fragment key={idx}>
                    {vertex.edge % 2 === 0 ? (
                        <text
                            fontSize={fontSize}
                            fill={isDark ? "white" : "black"}
                            {...edgeProps[vertex.edge](vertex.point[0], height)}
                        >
                            {tick}
                        </text>
                    ) : (
                        <text
                            fontSize={fontSize}
                            fill={isDark ? "white" : "black"}
                            {...edgeProps[vertex.edge](vertex.point[1], width)}
                        >
                            {tick}
                        </text>
                    )}
                </Fragment>
            )
        })
    }

    return (
        <svg
            viewBox={`0 0 ${width} ${height}`}
            height={height}
            width={width}
            className="absolute left-0 top-0 z-[101]"
        >
            {borderTicks.map((e) => e)}
        </svg>
    )
}
